import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Check } from 'lucide-react';
import api from '../api';

export default function PasswordResetPage() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [resetToken, setResetToken] = useState(''); // For development only

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await api.requestPasswordReset(email);
            setSuccess(true);
            if (response.reset_token) {
                setResetToken(response.reset_token); // Development only
            }
        } catch (err) {
            setError(err.message || 'Failed to send reset email');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'var(--gradient-radial)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }}>
            <div className="card" style={{ maxWidth: '450px', width: '100%', padding: '40px' }}>
                <Link to="/" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: 'var(--primary)',
                    marginBottom: '24px',
                    textDecoration: 'none'
                }}>
                    <ArrowLeft size={20} />
                    Back to Home
                </Link>

                <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
                    Reset Password
                </h1>
                <p style={{ color: 'var(--text-muted)', marginBottom: '32px' }}>
                    Enter your email address and we'll send you instructions to reset your password.
                </p>

                {success ? (
                    <div style={{
                        padding: '24px',
                        background: 'rgba(34, 197, 94, 0.1)',
                        border: '1px solid rgba(34, 197, 94, 0.3)',
                        borderRadius: '8px',
                        textAlign: 'center'
                    }}>
                        <Check size={48} color="var(--success)" style={{ margin: '0 auto 16px' }} />
                        <h3 style={{ marginBottom: '8px' }}>Check Your Email</h3>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                            If an account exists with <strong>{email}</strong>, you'll receive password reset instructions.
                        </p>

                        {/* Development only: Show reset token */}
                        {resetToken && (
                            <div style={{
                                marginTop: '24px',
                                padding: '16px',
                                background: 'var(--bg-secondary)',
                                borderRadius: '8px',
                                fontSize: '13px'
                            }}>
                                <strong>Development Mode:</strong><br />
                                <Link
                                    to={`/reset-password/${resetToken}`}
                                    style={{ color: 'var(--primary)', wordBreak: 'break-all' }}
                                >
                                    Reset Password (Click Here)
                                </Link>
                            </div>
                        )}
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label className="form-label">Email Address</label>
                            <div style={{ position: 'relative' }}>
                                <Mail
                                    size={18}
                                    style={{
                                        position: 'absolute',
                                        left: '14px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: 'var(--text-muted)'
                                    }}
                                />
                                <input
                                    type="email"
                                    className="form-input"
                                    style={{ paddingLeft: '44px' }}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    required
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="form-error" style={{ marginTop: '16px' }}>
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            className="btn btn-primary"
                            style={{ width: '100%', marginTop: '24px' }}
                            disabled={loading}
                        >
                            {loading ? 'Sending...' : 'Send Reset Instructions'}
                        </button>
                    </form>
                )}

                <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '14px', color: 'var(--text-muted)' }}>
                    Remember your password?{' '}
                    <Link to="/login" style={{ color: 'var(--primary)', fontWeight: '500' }}>
                        Sign in
                    </Link>
                </div>
            </div>
        </div>
    );
}
